<template>
  <div data-page-name="gift-campaign">
    <section class="hero">
      <div class="wrapper">
        <AppLogo target="_blank" variant="white" />

        <h1>{{ $t('gift-campaign.title') }}</h1>
        <h2>{{ $t('gift-campaign.subtitle') }}</h2>
        <p>{{ $t('gift-campaign.description') }}</p>

        <Buttons.Anchor anchor="#request-demo" accent="orange">
          {{ $t('gift-campaign.cta-button') }}
        </Buttons.Anchor>
      </div>
    </section>

    <section class="image">
      <NuxtImg
        src="/redesign/images/screenshots/dashboard-screenshot-and-veeam-ready-logo.png"
        :alt="$t('gift-campaign.screenshot.alt')"
        width="1344"
        height="785"
        format="webp"
        sizes="sm:377px md:750px xl:1344px"
        preload
      />
    </section>

    <section class="demo">
      <div class="wrapper">
        <div class="left">
          <h3>{{ $t('gift-campaign.demo.title') }}</h3>
          <strong>{{ $t('gift-campaign.demo.description') }}</strong>

          <div class="bonus">
            <h4>{{ $t('gift-campaign.bonus.title') }}</h4>
            <p>{{ $t('gift-campaign.bonus.description') }}</p>

            <div class="image">
              <NuxtImg
                src="/redesign/images/amazon-50-discount-stack.png"
                :alt="$t('gift-campaign.bonus.card-stack.alt')"
                width="384"
                height="306"
                sizes="xs: md: xl:282px"
                densities="x1 x2"
                format="webp"
              />
            </div>
          </div>
        </div>

        <Forms.Wrapper id="request-demo">
          <transition name="fade" mode="out-in">
            <Modals.ThankYou
              v-if="isSubmitted && !isLoading"
              :title="$t('Thank_You')"
              :description="$t('We_processing_and_get_back')"
              :link="{
                accent: 'orange',
                href: localePath('/'),
                text: $t('Visit_Home_Page'),
              }"
            />

            <Forms.RequestDemo
              v-else
              product="Object Storage"
              page="gift-campaign"
              :btn-text="$t('Request_Demo')"
              gtm-event="demo"
              accent="orange"
              @loading="setLoading"
              @submitted="makeSubmitted"
            />
          </transition>
        </Forms.Wrapper>
      </div>
    </section>

    <FooterLanding />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';

// components
import AppLogo from '~/components/redesign/AppLogo.vue';
import Modals from '~/components/redesign/Modals';
import Forms from '~/components/redesign/Forms';
import FooterLanding from '~/components/redesign/FooterLanding.vue';
import Buttons from '~/components/redesign/Buttons';

// stores
import useUIStore from '~/stores/ui';

// composables
import { useLocalePath } from '#i18n';
import useApiSeo from '~/composables/useApiSeo';

const uiStore = useUIStore();
const { setLoading } = uiStore;
const { isLoading } = storeToRefs(uiStore);
const localePath = useLocalePath();

const isSubmitted = ref(false);
const makeSubmitted = () => {
  isSubmitted.value = true;
  setLoading(false);
};

definePageMeta({ layout: 'empty' });
useHead({ meta: [{ name: 'robots', content: 'noindex' }] });
useApiSeo('gift-campaign');
</script>

<style lang="scss" scoped>
@import '$/mixins/media';
@import '$/mixins/typography';
@import '$/mixins/size';
@import '$/mixins/flex';
@import '$/functions/token';

@import '$/variables/shadows';

@mixin xs {
  @include media(30rem) {
    @content;
  }
}

[data-page-name='gift-campaign'] {
  section.hero {
    background: linear-gradient(131deg, #7b2e9c 50%, #ff7602);
    padding: 2rem 1rem 25rem 1rem;

    @include mobile {
      padding: 2rem 2rem 16.875rem;
    }

    @include xs {
      padding: 2rem 1rem 10.875rem;
    }

    .wrapper {
      @include wrapper(78rem);
      @include flex-center-center;

      flex-direction: column;
      gap: 1.5rem;

      color: token('text-reversed');
      text-align: center;

      @include xs {
        text-align: start;
      }

      [data-component-name='AppLogo'] {
        @include fixed-width(124px);
        margin-bottom: 3rem;
      }

      h1 {
        font-family: map-get($fonts, 'Inter');
        font-size: 4.3125rem;
        font-weight: 700;
        line-height: 4rem;

        @include xs {
          font-size: 2.5rem;
          line-height: 3rem;
        }
      }

      h2 {
        font-family: map-get($fonts, 'Inter');
        font-size: 2.75rem;
        line-height: 2.625rem;

        @include xs {
          font-size: 1.25rem;
          line-height: 1.6875rem;
        }
      }

      p {
        font-family: map-get($fonts, 'Inter');
        font-size: 1rem;
        font-weight: 400;
        line-height: 1.5rem;

        max-width: 87ch;

        margin-bottom: 2rem;
      }
    }
  }

  section.image {
    width: 100%;
    margin: -23rem auto 8rem;
    text-align: center;

    @include mobile {
      margin: -13rem auto 8rem;
    }

    @include xs {
      margin: -6rem auto 6rem;
    }

    img {
      max-width: 100%;
      height: auto;
    }
  }

  section.demo {
    .wrapper {
      @include wrapper(86rem);
      @include flex-stretch-sb;
      gap: 2rem;
      padding: 0 1rem;

      @include media(68.1875rem) {
        @include flex-center-center;
        flex-direction: column;
      }

      .left {
        max-width: 32.5rem;

        h3 {
          font-family: map-get($fonts, 'Poppins');
          color: map-get($accent, 'Secondary', 'Purple-50');
          font-size: 4rem;
          font-weight: 700;
          line-height: 4.625rem;

          @include mobile {
            font-size: 3.3125rem;
            line-height: 3.75rem;

            margin-bottom: 1rem;
          }

          @include xs {
            font-size: 1.5rem;
            line-height: 1.8125rem;
          }
        }

        strong {
          font-family: map-get($fonts, 'Poppins');
          color: map-get($accent, 'Secondary', 'Purple-50');
          font-size: 1.5rem;
          line-height: 2.1875rem;

          @include mobile {
            font-size: 3.3125rem;
            line-height: 3.75rem;
          }

          @include xs {
            font-size: 1.5rem;
            line-height: 1.8125rem;
          }
        }

        .bonus {
          display: block;
          border: 0.125rem solid token('text-action');
          padding: 4.5rem 4.5rem 12rem;
          position: relative;

          margin: 3rem 0 5rem;

          @include mobile {
            padding: 2rem 1rem 12rem;
            width: 100%;
          }

          h4 {
            font-family: map-get($fonts, 'Poppins');
            color: token('text-action');
            font-size: 4rem;
            font-weight: 700;
            line-height: 4.25rem;

            margin-bottom: 1rem;
          }

          p {
            font-family: map-get($fonts, 'Poppins');
            font-size: 1rem;
            font-weight: 400;
            line-height: 1.5rem;
          }

          .image {
            position: absolute;
            bottom: 0;
            left: 0;

            transform: translate(40%, 26%);

            width: 17.625rem;
            height: auto;

            max-width: 100%;

            @include mobile {
              max-width: 75%;
              transform: translate(35%, 26%);
            }

            @include xs {
              transform: translate(20%, 26%);
            }

            img {
              max-width: 100%;
              height: auto;
            }
          }
        }
      }

      [data-component-name='Forms.Wrapper'] {
        margin-bottom: -8rem;
        max-width: 35.375rem;

        @include mobile {
          width: calc(100% + 2rem);
        }
      }

      [data-component-name='Modals.ThankYou'] {
        height: 100%;
      }
    }
  }

  :deep([data-component-name='FooterLanding']) {
    padding-top: 12rem;
  }
}
</style>
